/**
 * DetailsInfoPanel engage module.
 * @module shared/pages/infoPanels/Details
 *
 */

import React, {useEffect, useState} from 'react';
import debug from 'debug';
import {connect} from 'react-redux';
import axiosInstance from '../../utils/axios';
import PropTypes from 'prop-types';
import {Button, Dropdown, LoadingSpinner, Row, TextInput, Icon, Columnizer} from '../../index';
import {_openModal} from '../../reducers/modals';
import {formatBytes, gatherData} from '../../utils/utils';
import moment from 'moment';
import {PORTAL_NAME} from '../../../config';

const log = debug('circleblack:shared:pages:InfoPanels:detailsInfoPanel');

const isInvestor = PORTAL_NAME === 'investor';

/**
 * DetailsInfoPanel component
 * @name Details
 */
//TODO Need layout reqs to account for using details info on other locations outside of table panel, e.g. view document modal;
const Details = ({
  id,
  document: docDataRow = {},
  dataManager: dataRowDataManager,
  openModal,
  compliance,
  isDetailsModal,
  session,
}) => {
  const [doc, setDoc] = useState({});
  const [baseTitle, setBaseTitle] = useState(doc.title || null);
  const [baseTags, setBaseTags] = useState([]);
  const [dropdownTags, setDropdownTags] = useState([]);
  const [canSaveDocument, setCanSaveDocument] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const placeholder = 'None';
  const getDocument = gatherData(`/documents/${id || docDataRow.id}?${compliance ? 'compliance=1' : ''}`);
  const isDocumentReadOnly = (doc.cat === 'Report') || (doc.ownerID !== session.profile.id);

  const saveDocument = () => {
    setCanSaveDocument(false);
    doc.title = doc.title.trim();
    doc.tags = dropdownTags.map(t => t.value);
    return axiosInstance.put(`/documents/${doc.id}`, {
      title: doc.title,
      tags: doc.tags,
    })
      .then(() => {
        setBaseTitle(doc.title);
        setBaseTags(doc.tags);
        dataRowDataManager.updateActiveDocument({...docDataRow, tags: doc.title, title: doc.tags});
        dataRowDataManager.setData(data => data.map((row) => {
          if (row.id === doc.id) {
            return {...row, title: doc.title, tags: doc.tags.map(t => ({label: t, value: t}))};
          }
          return row;
        }));
      })
      .catch(err => {
        log('Submission Error', err);
        openModal({
          type: 'Error',
          title: 'Save Error',
          err,
        });
      });
  };

  const updateTags = (name, value) => {
    setAvailableTags([...value.map(val => val.value)]);
    setDropdownTags(value);
    setDoc({...doc, tags: value.map(t => t.value)});

    if (value.map(x => x.value).sort().join() !== baseTags.sort().join()) {setCanSaveDocument(true);}
  };

  useEffect(() => {
    if (!doc.id || id !== doc.id || doc && docDataRow && doc.id !== docDataRow.id) {
      setLoading(true);

      getDocument()
        .then(d => {
          setBaseTitle(d.title);
          setBaseTags(d.tags);
          setDoc(d);
          setDropdownTags(d.tags.map(tag => ({label: tag, value: tag})));
          setLoading(false);
        })
        .catch(function error(err) {
          log('error was caught in useEffect', err);
          let res = err.response;
          if (!(res.status < 500 && res.data.error && res.data.error.msg)) {
            err.message = 'Unfortunately we encountered an error retrieving the document. If you have any questions, please contact CircleBlack Client Services at help@circleblack.com or 800-315-1241.';
          }
          openModal({
            type: 'Error',
            title: 'Failed to Retrieve Document',
            err: err,
          });
        });
    }
  }, [docDataRow.id, id]);

  useEffect(() => {
    const hasNameChange = doc.title && doc.title.trim() !== baseTitle.trim();
    const hasTagsChange = dropdownTags.map(t => t.value).sort().join() !== baseTags.sort().join();

    if (hasNameChange || hasTagsChange) {
      setCanSaveDocument(true);
    } else {
      setCanSaveDocument(false);
    }
  }, [doc, baseTitle, dropdownTags, baseTags]);

  const deleteIconRow = (isInvestor && <Row justifySubRow='left'>
    <Icon svg='circleDelete'
          height='20px'
          width='20px'
          action={() => {
            openModal({
              type: 'ConfirmModal',
              title: 'Confirm Deletion',
              body: 'This will permanently remove the document from your vault and from anyone you\'ve shared it with. This action cannot be undone.',
              button: {
                text: 'Delete',
                action: () => axiosInstance.delete(`/documents/${doc.id}`)
                  .then(() => {
                    dataRowDataManager.toggleDisplayInfoPanel(false);
                    dataRowDataManager.refresh();
                  })
                  .catch(err => {
                    openModal({
                      type: 'MessageModal',
                      title: 'Deletion Failed',
                      body: (err.response && err.response.data && err.response.data.error && err.response.data.error.msg) || err.message,
                    });
                  }),
              },
            });
          }}
          disabled={doc.ownerID !== session.profile.id || !!session.runAsInvestor}
          description={(doc.ownerID !== session.profile.id) ? 'Only the uploader of the document may delete it.' : null}
    />
  </Row>);

  const title = <TextInput
    key='Name'
    dataPoint={{name: 'title', value: doc.title}}
    type='text'
    title='Name'
    placeholder=''
    readOnly={isDocumentReadOnly}
    onChange={(name, value) => setDoc({...doc, [name]: value})}
    disabled={!!session.runAsInvestor}
    required/>;

  const associatedHousehold = <TextInput
    dataPoint={{name: 'assocHousehold', value: doc.assocHousehold || placeholder}}
    type='text'
    width={'100px'}
    layout={{grow: 1}}
    title='Associated Household'
    placeholder={doc.assocHousehold || placeholder}
    onChange={() => {}}
    readOnly={true}/>;

  const author = <TextInput
    dataPoint={{name: 'author', value: doc.author || placeholder}}
    width={'100px'}
    layout={{grow: 1}}
    type='text'
    title='Author'
    placeholder={doc.author || placeholder}
    onChange={() => {}}
    readOnly={true}/>;

  const created = <TextInput
    dataPoint={{
      name: 'createdAt',
      value: moment(doc.createdAt, 'YYYY-MM-DD HH:mm:ss').format('M/DD/yy h:mma'),
    }}
    type='text'
    width={'100px'}
    layout={{grow: 1}}
    title='Created'
    placeholder={doc.createdAt || placeholder}
    onChange={() => {}}
    readOnly={true}/>;

  const source = <TextInput
    dataPoint={{name: 'source', value: doc.source || 'N/A'}}
    type='text'
    title='Source'
    placeholder={doc.source || placeholder}
    onChange={() => {}}
    readOnly={true}/>;

  const docType = <TextInput
    dataPoint={{name: '', value: doc.type || 'N/A'}}
    width={'100px'}
    layout={{grow: 1}}
    type='text'
    title='Doc Type'
    placeholder={doc.type || placeholder}
    onChange={() => {}}
    readOnly={true}/>;

  const fileType = <TextInput
    dataPoint={{name: '', value: doc.fileFormat && doc.fileFormat.toUpperCase() || 'N/A'}}
    width={'100px'}
    layout={{grow: 1}}
    type='text'
    title='File Type'
    placeholder={doc.fileFormat && doc.fileFormat.toUpperCase() || 'N/A'}
    onChange={() => {}}
    readOnly={true}/>;

  const fileSize = <TextInput
    dataPoint={{name: '', value: formatBytes(Number(doc.fileSize)) || 'N/A'}}
    width={'100px'}
    layout={{grow: 1}}
    type='text'
    title='File Size'
    placeholder={formatBytes(Number(doc.fileSize)) || 'N/A'}
    onChange={() => {}}
    readOnly={true}/>;

  const period = <TextInput
    dataPoint={{name: '', value: doc.period || 'N/A'}}
    width={'100px'}
    layout={{grow: 1}}
    type='text'
    title='Period'
    placeholder={doc.period || 'N/A'}
    onChange={() => {}}
    readOnly={true}/>;

  const initiator = <TextInput
    dataPoint={{name: '', value: doc.initiator || 'N/A'}}
    type='text'
    title='Initiator'
    placeholder={doc.initiator || 'N/A'}
    onChange={() => {}}
    readOnly={true}/>;

  const context = <TextInput
    dataPoint={{name: '', value: doc.targetContext || placeholder}}
    type='text'
    title='Context'
    placeholder={doc.targetContext || placeholder}
    onChange={() => {}}
    readOnly={true}/>;

  const saveButton = <Button
    width={'fit-content'}
    importance={'secondary'}
    size={'md'}
    label={'Save'}
    disabled={!canSaveDocument || !!session.runAsInvestor}
    action={saveDocument}/>;

  const tags = <Dropdown
    title={'Tags'}
    isMulti={true}
    type={'dropdown'}
    placeholder={!isDocumentReadOnly && 'Enter Tags…'}
    dataPoint={{name: 'tags', value: dropdownTags}}
    options={availableTags}
    isCreatable={true}
    isClearable={true}
    onChange={updateTags}
    layout={{grow: 1}}
    readOnly={isDocumentReadOnly}
    disabled={!!session.runAsInvestor}
  />;

  const billingPeriod = <TextInput
    dataPoint={{name: '', value: doc.billingPeriod || placeholder}}
    type='text'
    title='Billing Period'
    placeholder={doc.billingPeriod || placeholder}
    readOnly={true}/>;

  const billingType = <TextInput
    dataPoint={{name: '', value: doc.billingType || placeholder}}
    type='text'
    title='Billing Type'
    placeholder={doc.billingType || placeholder}
    readOnly={true}/>;

  const billingFrequency = <TextInput
    dataPoint={{name: '', value: doc.billingFrequency || placeholder}}
    type='text'
    title='Billing Frequency'
    placeholder={doc.billingFrequency || placeholder}
    readOnly={true}/>;

  const baseRender = () => {
    if (loading) {
      return <LoadingSpinner/>;
    }
    return <>
      {deleteIconRow}
      {title}
      <Row>
        {doc.type === 'Article' ? author : associatedHousehold}
        {created}
      </Row>
      <Row>
        {doc.type === 'Article' ? source : docType}
        {doc.type === 'Article' ? '' : fileType}
      </Row>
      {doc.cat === 'Report' ? (
        <>
          <Row>
            {fileSize}
            {period}
          </Row>
          {initiator}
          {context}
        </>
      ) : ''}
      <Row>
        {doc && tags}
      </Row>
      {(doc.cat === 'Report' || doc.type === 'Article') ? '' : fileSize}
      <Row>
        {isDocumentReadOnly ? '' : saveButton}
      </Row>
    </>;
  };

  const modalRender = () => (
    <>
      <Columnizer itemGrow={true} itemWidth={300} width='fill' justifySubRow='left' wrap={true}>
        {title}
        {doc.type === 'Article' ? author : associatedHousehold}
        {created}
        {doc.type === 'Article' ? source : docType}
        {doc.type === 'Article' ? '' : fileType}
        {doc.cat === 'Report' ? fileSize : ''}
        {doc.cat === 'Report' ? period : ''}
        {doc.cat === 'Report' ? initiator : ''}
        {doc.cat === 'Report' ? context : ''}
        {(doc.cat === 'Report' || doc.type === 'Article') ? '' : fileSize}
      </Columnizer>
      {doc.cat === 'Billing Invoice' && <Columnizer itemGrow={true} itemWidth={300} width='fill' justifySubRow='left' wrap={true}>
        {billingPeriod}
        {billingType}
        {billingFrequency}
      </Columnizer>}
      <Row>
        {doc && tags}
      </Row>
      <Row>
        {isDocumentReadOnly ? '' : saveButton}
      </Row>
    </>
  );

  return isDetailsModal ? modalRender() : baseRender();
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modal => {
    dispatch(_openModal(modal));
  },
});

Details.propTypes = {
  /**
   * Document to display on the Info Panel. (required for table panels)
   */
  document: PropTypes.shape({
    id: PropTypes.number.isRequired,
    cat: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    tags: PropTypes.array,
    fileSize: PropTypes.string,
    type: PropTypes.string.isRequired,
    targetContext: PropTypes.string,
    initiator: PropTypes.string,
    period: PropTypes.string,
    fileFormat: PropTypes.string,
    author: PropTypes.string,
    assocHousehold: PropTypes.string,
    createdAt: PropTypes.string,
    source: PropTypes.string,
  }),
  /**
   * ID of document for document info. (required if document is not provided)
   */
  id: (props, propName, componentName) => {
    PropTypes.checkPropTypes({
      [propName]: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }, props, propName, componentName);
    if (!props.id && !props[propName]) {
      return new Error(`ID or Document is required for a ${componentName}.`);
    }
    if (props.document && props[propName]) {
      return new Error(`Can only have one of ID or Document in ${componentName} parameters.`);
    }
    return null;
  },
  /**
   /**
   * Is Session Reducer
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
    runAsInvestor: PropTypes.number,
  }).isRequired,
  /**
   * Function that opens modals on page
   */
  openModal: PropTypes.func.isRequired,
  /**
   * Object with functions that update the document in the parent table
   */
  dataManager: PropTypes.shape({
    setData: PropTypes.func.isRequired,
    updateActiveDocument: PropTypes.func.isRequired,
  }),
  /**
   * If true passes compliance to the api indicating that it's on a compliance related page.
   */
  compliance: PropTypes.bool,
  /**
   * If true renders layout for modal
   */
  isDetailsModal: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(Details);
